// Presentation-only split of a fragrance name into (name, concentration label).
// Rob, 2026-09-27: the concentration must never ride inside the fragrance name
// on any card; it shows once, as a pill beside the house. Catalog data is not
// rewritten here — this is the display layer while the catalog cleanup
// (FOS-CATALOG-NAME-CLEANUP) works through the stored names.
//
// "Gentleman Society Eau de Parfum Ambree" -> "Gentleman Society Ambree" + "Eau de Parfum"
// "Y Le Parfum"                            -> "Y"                        + "Le Parfum"
// "Sauvage EDT"                            -> "Sauvage"                  + "EDT"
// "Sauvage Elixir"                         -> unchanged (flanker, not a concentration)
// "Le Parfum"                              -> unchanged (it IS the whole name)

// Longest first so "Eau de Parfum" wins over the "Parfum" it ends with.
// Multi-word labels and the abbreviations are safe to lift from anywhere after
// the first word. Bare "Parfum" is only lifted from the end, because mid-name it
// is often part of the product's own name.
const ANYWHERE = ['eau de parfum', 'eau de toilette', 'eau de cologne', 'extrait de parfum', 'le parfum', 'edp', 'edt', 'edc']
const TRAILING_ONLY = ['parfum']

const ABBREV = { edp: 'EDP', edt: 'EDT', edc: 'EDC' }

const clean = value => String(value || '').trim().replace(/\s+/g, ' ')
const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function stripLeadingHouse(name, house) {
  const n = clean(name)
  const h = clean(house)
  if (!h || n.toLowerCase() === h.toLowerCase()) return n
  if (!n.toLowerCase().startsWith(`${h.toLowerCase()} `)) return n
  return clean(n.slice(h.length)) || n
}

function tidyLabel(raw, key) {
  if (ABBREV[key]) return ABBREV[key]
  return raw.split(' ').map((w, i) => (i > 0 && w.toLowerCase() === 'de') ? 'de' : w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

export function splitConcentrationFromName(name, house) {
  const first = splitOnce(stripLeadingHouse(name, house))
  if (!first.label) return first
  // "Boss The Scent Le Parfum for Her Le Parfum" carries it twice.
  const second = splitOnce(first.name)
  return { name: second.label && second.label.toLowerCase() === first.label.toLowerCase() ? second.name : first.name, label: first.label }
}

function splitOnce(base) {
  if (!base) return { name: '', label: null }

  for (const key of [...ANYWHERE, ...TRAILING_ONLY]) {
    const trailingOnly = TRAILING_ONLY.includes(key)
    // Must be preceded by at least one word, so a name that starts with (or
    // is) the label keeps it: "Le Parfum", "L'Eau de Parfum ..." stay whole.
    const pattern = trailingOnly
      ? new RegExp(`^(.+?)\\s+(${escapeRegex(key)})$`, 'i')
      : new RegExp(`^(.+?)\\s+(${escapeRegex(key)})(?=$|\\s)(.*)$`, 'i')
    const match = base.match(pattern)
    if (!match) continue
    const before = clean(match[1])
    // "L Eau de Parfum" / "L'Eau de Parfum" is a flanker's own name, not a
    // concentration bolted on; and a name must not end on a dangling article.
    if (/(^|\s)(l|l'|d|the|de|du|la|le)$/i.test(before)) continue
    const after = trailingOnly ? '' : clean(match[3])
    const rest = clean(`${before} ${after}`)
    // Never leave an orphaned article or number as the whole name ("Le Parfum" is a name).
    if (!rest || /^[\d\s]+$/.test(rest) || /^(le|la|les|l'|the|eau|de|du)$/i.test(rest)) continue
    return { name: rest, label: tidyLabel(match[2], key.toLowerCase()) }
  }
  return { name: base, label: null }
}

const UNKNOWN = new Set(['', 'unknown', 'ambiguous', 'n/a', 'na', 'none'])

// The one pill label for a row: the house's own wording lifted from the name
// wins (YSL says "Le Parfum", not "Parfum"); otherwise the stored value.
export function fragranceTitle(row = {}) {
  const { name, label } = splitConcentrationFromName(row?.name, row?.house)
  const stored = clean(row?.concentration_display || row?.concentration)
  const concentration = label || (UNKNOWN.has(stored.toLowerCase()) ? null : stored)
  return { name: name || clean(row?.name), concentration }
}

export function fragranceDisplayName(row = {}) {
  return fragranceTitle(row).name
}
